import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { COLORS } from "@tecumseh/theme/design";
import * as React from "react";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct;

export interface ProductDetailsPageTitleStyles {
    container?: GridContainerProps;
    brandGridItem?: GridItemProps;
    brandStyles?: ProductBrandStyles;
    titleGridItem?: GridItemProps;
    titleText?: TypographyProps;
    modelNumber?: TypographyProps;
}

export const pageTitleStyles: ProductDetailsPageTitleStyles = {
    container: {
        gap: 0,
        offsetCss: css`
            max-width: 100%;
        `,
    },
    brandGridItem: {
        width: 12,
    },
    brandStyles: {
        logoImage: {
            css: css`
                img {
                    max-width: 150px;
                    max-height: 150px;
                }
            `,
        },
        nameText: {
            size: 16,
            weight: "normal",
        },
    },
    titleGridItem: {
        width: 12,
        css: css`
            display: block;
        `,
    },
    titleText: {
        variant: "h2",
    },
    modelNumber: {
        color: COLORS.black,
        weight: 500,
        css: css`
            margin-bottom: 10px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            display: block;
                            font-size: 18px;
                        `,
                        null,
                        css`
                            display: inline;
                        `,
                    ],
                    "min",
                )}
        `,
    },
};

const styles = pageTitleStyles;

const ProductDetailsPageTitle: React.FC<Props> = ({ product }) => {
    const modelNumber = product?.detail?.modelNumber;
    const [isProductDiscontinued, setIsProductDiscontinued] = React.useState<boolean>(false);
    const [isProductNumberSameWithGlobalProductNumber, setIsProductNumberSameWithGlobalProductNumber] = React.useState<boolean>(false);

    function checkDiscontinued(attributeTypes:any) {
        // Iterate through each attribute in the array
        for (let attribute of attributeTypes) {
            // Check if the 'name' property (in lowercase) is 'web_product_status'
            if (attribute.name.toLowerCase() === "web_product_status") {
                // Check if any of the attribute values (in lowercase) have the value "discontinued"
                const isDiscontinued = attribute.attributeValues.some((value:any) => value.value.toLowerCase() === "discontinued");
                
                // Return true if "discontinued" is found, otherwise false
                if (isDiscontinued) {
                    return true;
                }
            }
        }

        // Return false if no match was found
        return false;
    }

    // Function to check if 'GLOBAL_MODEL' exists and if its value matches the given model number
    function checkGlobalModel(attributeTypes: any, productNumber: string): boolean {
        // Iterate through each attribute in the array
        for (let attribute of attributeTypes) {
            // Check if the 'name' property (in lowercase) is 'global_model'
            if (attribute.name.toLowerCase() === "global_model") {
                // Check if any of the attribute values (in lowercase) match the provided model number (case-insensitive)
                const isModelMatch = attribute.attributeValues.some((value:any) => value.value.toLowerCase() === productNumber.toLowerCase());
                
                // Return true if model number matches, otherwise false
                if (isModelMatch) {
                    return true;
                }
            }
        }

        // Return false if no match was found
        return false;
    }

    React.useEffect(()=>
    {
        setIsProductDiscontinued(checkDiscontinued(product.attributeTypes));
        setIsProductNumberSameWithGlobalProductNumber(checkGlobalModel(product.attributeTypes, product.productNumber));
    },[]);
    
    return (
        <GridContainer {...styles.container}>
            {product.brand && (
                <GridItem {...styles.brandGridItem}>
                    <ProductBrand brand={product.brand} showLogo={true} extendedStyles={styles.brandStyles} />
                </GridItem>
            )}
            <GridItem {...styles.titleGridItem}>
                <Typography {...styles.titleText} data-test-selector="productDetails_productDescription">
                    {product.productTitle}
                </Typography>
                {isProductDiscontinued && isProductNumberSameWithGlobalProductNumber && modelNumber && (
                    <Typography {...styles.modelNumber}>{` (${modelNumber})`}</Typography>
                )
                }
                {isProductDiscontinued && !isProductNumberSameWithGlobalProductNumber && product.detail?.sku && (
                    <Typography {...styles.modelNumber}>{` (${product.detail?.sku})`}</Typography>
                )
                }
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsPageTitle),
    definition: {
        displayName: "Page Title",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
